const chapter = {
  id: 'trigonometric-ratios-identities',
  slug: 'trigonometric-ratios-identities',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE Main + Advanced',
  title: {
    english: 'Trigonometric Ratios & Identities',
        hindi: 'त्रिकोणमितीय अनुपात और सर्वसमिकाएँ',
  },
  estReadMinutes: 60,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2 questions in JEE Main, 1 in Advanced',
        hindi: 'जेईई मेन में 2 प्रश्न, एडवांस्ड में 1 प्रश्न',
  },
  syllabusTags: ['Trigonometry', 'JEE-Main', 'JEE-Advanced', 'Class-11'],
  sections: [
    {
      id: 'angles',
      title: {
        english: 'Angles & Measurement',
        hindi: 'कोण और माप',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'An **angle** is the figure formed by rotating a ray about its end-point. Two systems of measurement are used: **degree** ($1$ revolution $= 360°$) and **radian** ($1$ revolution $= 2\\pi$ rad).',
        hindi: 'एक **कोण** वह आकृति है जो किसी किरण को उसके प्रारंभिक बिंदु के बारे में घुमाने से बनती है। मापन की दो प्रणालियाँ प्रचलित हैं: **डिग्री** ($1$ परिक्रमण $= 360°$) और **रेडियन** ($1$ परिक्रमण $= 2\\pi$ रेडियन)।',
          },
        },
        {
          type: 'equation',
          latex: '\\pi^{\\,c} = 180° \\quad\\Longleftrightarrow\\quad 1^{\\,c} = \\dfrac{180°}{\\pi} \\approx 57.296°',
        },
        {
          type: 'equation',
          latex: '\\text{Arc length: } \\ell = r\\theta,\\quad \\text{Sector area: } A = \\tfrac{1}{2}r^2\\theta \\quad (\\theta \\text{ in radians})',
        },
        {
          type: 'tip',
          text: {
            english: 'In calculus, **always use radians**. Degree mode breaks every derivative formula in this chapter.',
        hindi: 'कैलकुलस में **हमेशा रेडियन** का ही प्रयोग करें। डिग्री मोड इस अध्याय के अवकलज सूत्रों को अमान्य कर देता है।',
          },
        },
      ],
    },
    {
      id: 'definitions',
      title: {
        english: 'Trigonometric Ratios on the Unit Circle',
        hindi: 'इकाई वृत्त पर त्रिकोणमितीय अनुपात',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Place an angle $\\theta$ in standard position; its terminal arm meets the unit circle at $P(\\cos\\theta, \\sin\\theta)$. All six trig ratios are coordinates and reciprocals.',
        hindi: 'कोण $\\theta$ को मानक स्थिति में रखें; इसकी अंतिम भुजा इकाई वृत्त को बिंदु $P(\\cos\\theta, \\sin\\theta)$ पर काटती है। सभी छह त्रिकोणमितीय अनुपात निर्देशांकों और उनके व्युत्क्रमों से प्राप्त होते हैं।',
          },
        },
        {
          type: 'figure',
          svg: `
<svg viewBox="0 0 380 360" xmlns="http://www.w3.org/2000/svg">
  <line x1="20" y1="180" x2="360" y2="180" stroke="#374151" stroke-width="1.4"/>
  <line x1="190" y1="340" x2="190" y2="20" stroke="#374151" stroke-width="1.4"/>
  <circle cx="190" cy="180" r="130" fill="none" stroke="#4338ca" stroke-width="1.8"/>
  <line x1="190" y1="180" x2="255.0" y2="67.4" stroke="#dc2626" stroke-width="2.2"/>
  <line x1="255.0" y1="180" x2="255.0" y2="67.4" stroke="#10b981" stroke-width="2" stroke-dasharray="4 3"/>
  <line x1="190" y1="67.4" x2="255.0" y2="67.4" stroke="#f59e0b" stroke-width="2" stroke-dasharray="4 3"/>
  <circle cx="255.0" cy="67.4" r="4.5" fill="#1f2937"/>
  <text x="263" y="61.41669750802298" font-size="12" fill="#1f2937" font-weight="600">P(cos θ, sin θ)</text>
  <text x="187.5" y="119.7083487540115" font-size="12" fill="#b91c1c">r = 1</text>
  <text x="261" y="127.7083487540115" font-size="11" fill="#047857">sin θ</text>
  <text x="210.5" y="63.41669750802298" font-size="11" fill="#92400e">cos θ</text>
  <path d="M 214 180 A 24 24 0 0 0 202.0 159.2" fill="none" stroke="#374151" stroke-width="1.5"/>
  <text x="220" y="172" font-size="11" fill="#374151">θ</text>
  <text x="178" y="198" font-size="11" fill="#6b7280">O</text>
  <!-- quadrant labels -->
  <text x="302" y="68" font-size="11" fill="#6b7280">Q I</text>
  <text x="68" y="68" font-size="11" fill="#6b7280">Q II</text>
  <text x="68" y="304" font-size="11" fill="#6b7280">Q III</text>
  <text x="298" y="304" font-size="11" fill="#6b7280">Q IV</text>
</svg>`,
          caption: {
            english: 'Unit-circle definition: cos θ is the x-coordinate, sin θ is the y-coordinate of P.',
        hindi: 'यूनिट-सर्कल परिभाषा: cos θ, x-निर्देशांक है, पाप θ, P का y-निर्देशांक है।',
          },
        },
        {
          type: 'table',
          headers: ['Ratio', 'Definition', 'Reciprocal'],
          rows: [
            ['$\\sin\\theta$', '$y$ coordinate of $P$', '$\\csc\\theta = 1/\\sin\\theta$'],
            ['$\\cos\\theta$', '$x$ coordinate of $P$', '$\\sec\\theta = 1/\\cos\\theta$'],
            ['$\\tan\\theta$', '$y/x = \\sin\\theta/\\cos\\theta$', '$\\cot\\theta = \\cos\\theta/\\sin\\theta$'],
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Pythagorean Identities',
        hindi: 'पाइथागोरस सर्वसमिकाएँ',
          },
          statement: {
            english: '$$\\sin^2\\theta + \\cos^2\\theta = 1,\\quad 1 + \\tan^2\\theta = \\sec^2\\theta,\\quad 1 + \\cot^2\\theta = \\csc^2\\theta.$$',
        hindi: '$$\\sin^2\\theta + \\cos^2\\theta = 1,\\quad 1 + \\tan^2\\theta = \\sec^2\\theta,\\quad 1 + \\cot^2\\theta = \\csc^2\\theta.$$',
          },
        },
      ],
    },
    {
      id: 'standard-values',
      title: {
        english: 'Standard Angles — Values to Memorise',
        hindi: 'मानक कोण — याद करने के मान',
      },
      blocks: [
        {
          type: 'table',
          headers: ['$\\theta$', '$0$', '$\\pi/6$', '$\\pi/4$', '$\\pi/3$', '$\\pi/2$'],
          rows: [
            ['$\\sin$', '$0$', '$1/2$', '$1/\\sqrt 2$', '$\\sqrt 3/2$', '$1$'],
            ['$\\cos$', '$1$', '$\\sqrt 3/2$', '$1/\\sqrt 2$', '$1/2$', '$0$'],
            ['$\\tan$', '$0$', '$1/\\sqrt 3$', '$1$', '$\\sqrt 3$', '$\\infty$'],
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'Mnemonic: write $\\sin\\theta$ row as $\\sqrt{0}/2,\\ \\sqrt{1}/2,\\ \\sqrt{2}/2,\\ \\sqrt{3}/2,\\ \\sqrt{4}/2$. Reverse for $\\cos$.',
        hindi: 'स्मरणीय: $\\sin\\theta$ पंक्ति को $\\sqrt{0}/2,\\ \\sqrt{1}/2,\\ \\sqrt{2}/2,\\ \\sqrt{3}/2,\\ \\sqrt{4}/2$ के रूप में लिखें। $\\cos$ के लिए उलटा।',
          },
        },
      ],
    },
    {
      id: 'signs-quadrants',
      title: {
        english: 'Signs of Ratios in Each Quadrant',
        hindi: 'प्रत्येक चतुर्थांश में अनुपातों के चिह्न',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Quadrant', 'Ratios that are POSITIVE'],
          rows: [
            ['I  ($0 < \\theta < \\pi/2$)', '**A**ll'],
            ['II ($\\pi/2 < \\theta < \\pi$)', '**S**in (and csc)'],
            ['III ($\\pi < \\theta < 3\\pi/2$)', '**T**an (and cot)'],
            ['IV ($3\\pi/2 < \\theta < 2\\pi$)', '**C**os (and sec)'],
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'Mnemonic: **ASTC** ("All Students Take Cricket / All Silver Tea Cups").',
        hindi: 'स्मरण सूत्र: **ASTC** - I में All, II में Sin, III में Tan, और IV में Cos धनात्मक होता है।',
          },
        },
      ],
    },
    {
      id: 'allied-angles',
      title: {
        english: 'Allied Angles & Periodicity',
        hindi: 'सहायक कोण और आवर्तता',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Allied angles are of the form $\\dfrac{n\\pi}{2} \\pm \\theta$. Use the rule:',
        hindi: 'संबद्ध कोण $\\dfrac{n\\pi}{2} \\pm \\theta$ के रूप के होते हैं। नियम का प्रयोग करें:',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Even multiple of $\\pi/2$** ($\\pi, 2\\pi, \\ldots$): name **does not change** ($\\sin \\to \\sin$, $\\cos \\to \\cos$).',
        hindi: '**$\\pi/2$** का सम गुणज ($\\pi, 2\\pi, \\ldots$): नाम **नहीं बदलता** ($\\sin \\to \\sin$, $\\cos \\to \\cos$)।',
            },
            {
              english: '**Odd multiple of $\\pi/2$** ($\\pi/2, 3\\pi/2$): name **co-changes** ($\\sin \\to \\cos$, $\\tan \\to \\cot$, $\\sec \\to \\csc$).',
        hindi: '**$\\pi/2$** का विषम गुणज ($\\pi/2, 3\\pi/2$): नाम **सह-परिवर्तन** ($\\sin \\to \\cos$, $\\tan \\to \\cot$, $\\sec \\to \\csc$)।',
            },
            {
              english: 'Sign is decided by the **quadrant** of the resulting angle (use ASTC).',
        hindi: 'चिह्न परिणामी कोण के **चतुर्थांश** द्वारा तय किया जाता है (एएसटीसी का उपयोग करें)।',
            },
          ],
        },
        {
          type: 'equation',
          latex: '\\sin(\\pi - \\theta) = \\sin\\theta,\\quad \\cos(\\pi - \\theta) = -\\cos\\theta,\\quad \\tan(\\pi - \\theta) = -\\tan\\theta',
        },
        {
          type: 'equation',
          latex: '\\sin(\\pi/2 - \\theta) = \\cos\\theta,\\quad \\cos(\\pi/2 - \\theta) = \\sin\\theta',
        },
        {
          type: 'figure',
          svg: `
<svg viewBox="0 0 520 220" xmlns="http://www.w3.org/2000/svg">
  <line x1="20" y1="110" x2="510" y2="110" stroke="#374151" stroke-width="1.2"/>
  <line x1="180" y1="20" x2="180" y2="200" stroke="#374151" stroke-width="1.2"/>
  <polyline points="20.0,110.0 22.4,112.8 24.8,115.6 27.2,118.5 29.6,121.2 32.0,124.0 34.4,126.7 36.8,129.4 39.2,132.1 41.6,134.7 44.0,137.2 46.4,139.7 48.8,142.1 51.2,144.5 53.6,146.8 56.0,149.0 58.4,151.1 60.8,153.1 63.2,155.0 65.6,156.8 68.0,158.5 70.4,160.1 72.8,161.6 75.2,163.0 77.6,164.3 80.0,165.4 82.4,166.5 84.8,167.3 87.2,168.1 89.6,168.8 92.0,169.3 94.4,169.6 96.8,169.9 99.2,170.0 101.6,170.0 104.0,169.8 106.4,169.5 108.8,169.1 111.2,168.6 113.6,167.9 116.0,167.1 118.4,166.1 120.8,165.1 123.2,163.9 125.6,162.6 128.0,161.2 130.4,159.6 132.8,158.0 135.2,156.2 137.6,154.4 140.0,152.4 142.4,150.4 144.8,148.2 147.2,146.0 149.6,143.7 152.0,141.3 154.4,138.9 156.8,136.4 159.2,133.8 161.6,131.2 164.0,128.5 166.4,125.8 168.8,123.1 171.2,120.3 173.6,117.5 176.0,114.7 178.4,111.9 180.8,109.1 183.2,106.2 185.6,103.4 188.0,100.6 190.4,97.8 192.8,95.1 195.2,92.4 197.6,89.7 200.0,87.0 202.4,84.5 204.8,81.9 207.2,79.5 209.6,77.1 212.0,74.7 214.4,72.5 216.8,70.3 219.2,68.2 221.6,66.3 224.0,64.4 226.4,62.6 228.8,60.9 231.2,59.3 233.6,57.9 236.0,56.5 238.4,55.3 240.8,54.2 243.2,53.2 245.6,52.4 248.0,51.7 250.4,51.1 252.8,50.6 255.2,50.3 257.6,50.1 260.0,50.0 262.4,50.1 264.8,50.3 267.2,50.6 269.6,51.1 272.0,51.7 274.4,52.4 276.8,53.2 279.2,54.2 281.6,55.3 284.0,56.5 286.4,57.9 288.8,59.3 291.2,60.9 293.6,62.6 296.0,64.4 298.4,66.3 300.8,68.2 303.2,70.3 305.6,72.5 308.0,74.7 310.4,77.1 312.8,79.5 315.2,81.9 317.6,84.5 320.0,87.0 322.4,89.7 324.8,92.4 327.2,95.1 329.6,97.8 332.0,100.6 334.4,103.4 336.8,106.2 339.2,109.1 341.6,111.9 344.0,114.7 346.4,117.5 348.8,120.3 351.2,123.1 353.6,125.8 356.0,128.5 358.4,131.2 360.8,133.8 363.2,136.4 365.6,138.9 368.0,141.3 370.4,143.7 372.8,146.0 375.2,148.2 377.6,150.4 380.0,152.4 382.4,154.4 384.8,156.2 387.2,158.0 389.6,159.6 392.0,161.2 394.4,162.6 396.8,163.9 399.2,165.1 401.6,166.1 404.0,167.1 406.4,167.9 408.8,168.6 411.2,169.1 413.6,169.5 416.0,169.8 418.4,170.0 420.8,170.0 423.2,169.9 425.6,169.6 428.0,169.3 430.4,168.8 432.8,168.1 435.2,167.3 437.6,166.5 440.0,165.4 442.4,164.3 444.8,163.0 447.2,161.6 449.6,160.1 452.0,158.5 454.4,156.8 456.8,155.0 459.2,153.1 461.6,151.1 464.0,149.0 466.4,146.8 468.8,144.5 471.2,142.1 473.6,139.7 476.0,137.2 478.4,134.7 480.8,132.1 483.2,129.4 485.6,126.7 488.0,124.0 490.4,121.2 492.8,118.5 495.2,115.6 497.6,112.8 500.0,110.0" fill="none" stroke="#dc2626" stroke-width="2"/>
  <polyline points="20.0,170.0 22.4,169.9 24.8,169.7 27.2,169.4 29.6,168.9 32.0,168.3 34.4,167.6 36.8,166.8 39.2,165.8 41.6,164.7 44.0,163.5 46.4,162.1 48.8,160.7 51.2,159.1 53.6,157.4 56.0,155.6 58.4,153.7 60.8,151.8 63.2,149.7 65.6,147.5 68.0,145.3 70.4,142.9 72.8,140.5 75.2,138.1 77.6,135.5 80.0,133.0 82.4,130.3 84.8,127.6 87.2,124.9 89.6,122.2 92.0,119.4 94.4,116.6 96.8,113.8 99.2,110.9 101.6,108.1 104.0,105.3 106.4,102.5 108.8,99.7 111.2,96.9 113.6,94.2 116.0,91.5 118.4,88.8 120.8,86.2 123.2,83.6 125.6,81.1 128.0,78.7 130.4,76.3 132.8,74.0 135.2,71.8 137.6,69.6 140.0,67.6 142.4,65.6 144.8,63.8 147.2,62.0 149.6,60.4 152.0,58.8 154.4,57.4 156.8,56.1 159.2,54.9 161.6,53.9 164.0,52.9 166.4,52.1 168.8,51.4 171.2,50.9 173.6,50.5 176.0,50.2 178.4,50.0 180.8,50.0 183.2,50.1 185.6,50.4 188.0,50.7 190.4,51.2 192.8,51.9 195.2,52.7 197.6,53.5 200.0,54.6 202.4,55.7 204.8,57.0 207.2,58.4 209.6,59.9 212.0,61.5 214.4,63.2 216.8,65.0 219.2,66.9 221.6,68.9 224.0,71.0 226.4,73.2 228.8,75.5 231.2,77.9 233.6,80.3 236.0,82.8 238.4,85.3 240.8,87.9 243.2,90.6 245.6,93.3 248.0,96.0 250.4,98.8 252.8,101.5 255.2,104.4 257.6,107.2 260.0,110.0 262.4,112.8 264.8,115.6 267.2,118.5 269.6,121.2 272.0,124.0 274.4,126.7 276.8,129.4 279.2,132.1 281.6,134.7 284.0,137.2 286.4,139.7 288.8,142.1 291.2,144.5 293.6,146.8 296.0,149.0 298.4,151.1 300.8,153.1 303.2,155.0 305.6,156.8 308.0,158.5 310.4,160.1 312.8,161.6 315.2,163.0 317.6,164.3 320.0,165.4 322.4,166.5 324.8,167.3 327.2,168.1 329.6,168.8 332.0,169.3 334.4,169.6 336.8,169.9 339.2,170.0 341.6,170.0 344.0,169.8 346.4,169.5 348.8,169.1 351.2,168.6 353.6,167.9 356.0,167.1 358.4,166.1 360.8,165.1 363.2,163.9 365.6,162.6 368.0,161.2 370.4,159.6 372.8,158.0 375.2,156.2 377.6,154.4 380.0,152.4 382.4,150.4 384.8,148.2 387.2,146.0 389.6,143.7 392.0,141.3 394.4,138.9 396.8,136.4 399.2,133.8 401.6,131.2 404.0,128.5 406.4,125.8 408.8,123.1 411.2,120.3 413.6,117.5 416.0,114.7 418.4,111.9 420.8,109.1 423.2,106.2 425.6,103.4 428.0,100.6 430.4,97.8 432.8,95.1 435.2,92.4 437.6,89.7 440.0,87.0 442.4,84.5 444.8,81.9 447.2,79.5 449.6,77.1 452.0,74.7 454.4,72.5 456.8,70.3 459.2,68.2 461.6,66.3 464.0,64.4 466.4,62.6 468.8,60.9 471.2,59.3 473.6,57.9 476.0,56.5 478.4,55.3 480.8,54.2 483.2,53.2 485.6,52.4 488.0,51.7 490.4,51.1 492.8,50.6 495.2,50.3 497.6,50.1 500.0,50.0" fill="none" stroke="#4338ca" stroke-width="2"/>
  <text x="264" y="46" font-size="10" fill="#b91c1c">sin x</text>
  <text x="188" y="46" font-size="10" fill="#3730a3">cos x</text>
  <text x="336" y="124" font-size="10" fill="#6b7280">π</text>
  <text x="494" y="124" font-size="10" fill="#6b7280">2π</text>
  <text x="16" y="124" font-size="10" fill="#6b7280">-π</text>
</svg>`,
          caption: {
            english: 'Graphs of sin x (red) and cos x (blue) over [-π, 2π]: each has period 2π, range [-1, 1], and they are π/2 phase-shifted.',
        hindi: '[-π, 2π] पर $\\sin x$ (लाल) और $\\cos x$ (नीला) के ग्राफ: दोनों का आवर्तकाल $2\\pi$, परास $[-1,1]$ है, तथा दोनों में $\\pi/2$ का फेज़-अंतर है।',
          },
        },
        {
          type: 'table',
          headers: ['Function', 'Period'],
          rows: [
            ['$\\sin x,\\ \\cos x,\\ \\sec x,\\ \\csc x$', '$2\\pi$'],
            ['$\\tan x,\\ \\cot x$', '$\\pi$'],
            ['$|\\sin x|,\\ |\\cos x|$', '$\\pi$'],
            ['$\\sin(kx)$', '$2\\pi/|k|$'],
          ],
        },
      ],
    },
    {
      id: 'sum-difference',
      title: {
        english: 'Sum & Difference Formulas',
        hindi: 'योग और अंतर सूत्र',
      },
      blocks: [
        {
          type: 'equation',
          latex: '\\sin(A \\pm B) = \\sin A \\cos B \\pm \\cos A \\sin B',
        },
        {
          type: 'equation',
          latex: '\\cos(A \\pm B) = \\cos A \\cos B \\mp \\sin A \\sin B',
        },
        {
          type: 'equation',
          latex: '\\tan(A \\pm B) = \\dfrac{\\tan A \\pm \\tan B}{1 \\mp \\tan A \\tan B}',
        },
        {
          type: 'equation',
          latex: '\\cot(A \\pm B) = \\dfrac{\\cot A \\cot B \\mp 1}{\\cot B \\pm \\cot A}',
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Find $\\sin 75°$.',
        hindi: '$\\sin 75°$ खोजें।',
          },
          solution: {
            english: '$\\sin 75° = \\sin(45° + 30°) = \\sin 45 \\cos 30 + \\cos 45 \\sin 30 = \\dfrac{1}{\\sqrt 2} \\cdot \\dfrac{\\sqrt 3}{2} + \\dfrac{1}{\\sqrt 2} \\cdot \\dfrac{1}{2} = \\dfrac{\\sqrt 3 + 1}{2\\sqrt 2} = \\dfrac{\\sqrt 6 + \\sqrt 2}{4}$.',
        hindi: '$\\sin 75° = \\sin(45° + 30°) = \\sin 45 \\cos 30 + \\cos 45 \\sin 30 = \\dfrac{1}{\\sqrt 2} \\cdot \\dfrac{\\sqrt 3}{2} + \\dfrac{1}{\\sqrt 2} \\cdot \\dfrac{1}{2} = \\dfrac{\\sqrt 3 + 1}{2\\sqrt 2} = \\dfrac{\\sqrt 6 + \\sqrt 2}{4}$.',
          },
        },
      ],
    },
    {
      id: 'multiple-submultiple',
      title: {
        english: 'Multiple & Submultiple Angles',
        hindi: 'बहुगुण और अवगुण कोण',
      },
      blocks: [
        {
          type: 'equation',
          latex: '\\sin 2A = 2\\sin A \\cos A = \\dfrac{2\\tan A}{1 + \\tan^2 A}',
        },
        {
          type: 'equation',
          latex: '\\cos 2A = \\cos^2 A - \\sin^2 A = 2\\cos^2 A - 1 = 1 - 2\\sin^2 A = \\dfrac{1 - \\tan^2 A}{1 + \\tan^2 A}',
        },
        {
          type: 'equation',
          latex: '\\tan 2A = \\dfrac{2\\tan A}{1 - \\tan^2 A}',
        },
        {
          type: 'equation',
          latex: '\\sin 3A = 3\\sin A - 4\\sin^3 A,\\qquad \\cos 3A = 4\\cos^3 A - 3\\cos A',
        },
        {
          type: 'equation',
          latex: '\\tan 3A = \\dfrac{3\\tan A - \\tan^3 A}{1 - 3\\tan^2 A}',
        },
        {
          type: 'tip',
          text: {
            english: '**Half-angle**: $\\sin^2(A/2) = \\dfrac{1 - \\cos A}{2}$, $\\cos^2(A/2) = \\dfrac{1 + \\cos A}{2}$, $\\tan^2(A/2) = \\dfrac{1 - \\cos A}{1 + \\cos A}$. Useful for proving identities and integration.',
        hindi: '**आधा कोण**: $\\sin^2(A/2) = \\dfrac{1 - \\cos A}{2}$, $\\cos^2(A/2) = \\dfrac{1 + \\cos A}{2}$, $\\tan^2(A/2) = \\dfrac{1 - \\cos A}{1 + \\cos A}$। पहचान और एकीकरण साबित करने के लिए उपयोगी।',
          },
        },
      ],
    },
    {
      id: 'product-sum',
      title: {
        english: 'Product ↔ Sum Formulas',
        hindi: 'उत्पाद ↔ योग सूत्र',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Product → Sum', 'Sum → Product'],
          rows: [
            ['$2\\sin A \\cos B = \\sin(A+B) + \\sin(A-B)$', '$\\sin C + \\sin D = 2\\sin\\dfrac{C+D}{2}\\cos\\dfrac{C-D}{2}$'],
            ['$2\\cos A \\sin B = \\sin(A+B) - \\sin(A-B)$', '$\\sin C - \\sin D = 2\\cos\\dfrac{C+D}{2}\\sin\\dfrac{C-D}{2}$'],
            ['$2\\cos A \\cos B = \\cos(A-B) + \\cos(A+B)$', '$\\cos C + \\cos D = 2\\cos\\dfrac{C+D}{2}\\cos\\dfrac{C-D}{2}$'],
            ['$2\\sin A \\sin B = \\cos(A-B) - \\cos(A+B)$', '$\\cos C - \\cos D = -2\\sin\\dfrac{C+D}{2}\\sin\\dfrac{C-D}{2}$'],
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Prove that $\\sin 20° \\sin 40° \\sin 60° \\sin 80° = \\dfrac{3}{16}$.',
        hindi: 'सिद्ध कीजिए कि $\\sin 20° \\sin 40° \\sin 60° \\sin 80° = \\dfrac{3}{16}$.',
          },
          solution: {
            english: '$\\sin 60° = \\sqrt 3/2$. Use $\\sin 20\\sin 40\\sin 80 = \\dfrac{\\sin 60°}{4} \\cdot$ (standard $\\sin 3A = 4\\sin A \\sin(60°-A)\\sin(60°+A)$ with $A = 20°$). Final value $= \\sqrt 3/8 \\cdot \\sqrt 3/2 = 3/16$.',
        hindi: '$\\sin 60° = \\sqrt 3/2$. $\\sin 20\\sin 40\\sin 80 = \\dfrac{\\sin 60°}{4} \\cdot$ ($A = 20°$ के साथ मानक $\\sin 3A = 4\\sin A \\sin(60°-A)\\sin(60°+A)$) का उपयोग करें। अंतिम मान $= \\sqrt 3/8 \\cdot \\sqrt 3/2 = 3/16$.',
          },
        },
      ],
    },
    {
      id: 'maxmin',
      title: {
        english: 'Maximum & Minimum of $a\\sin\\theta + b\\cos\\theta$',
        hindi: 'अधिकतम एवं न्यूनतम $a\\sin\\theta + b\\cos\\theta$',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Bound',
        hindi: 'बाध्य',
          },
          statement: {
            english: 'For real $a, b$ and any real $\\theta$:  $-\\sqrt{a^2 + b^2} \\le a\\sin\\theta + b\\cos\\theta \\le \\sqrt{a^2 + b^2}$.',
        hindi: 'वास्तविक $a, b$ और किसी भी वास्तविक $\\theta$ के लिए: $-\\sqrt{a^2 + b^2} \\le a\\sin\\theta + b\\cos\\theta \\le \\sqrt{a^2 + b^2}$।',
          },
          proof: {
            english: 'Write $a\\sin\\theta + b\\cos\\theta = R\\sin(\\theta + \\phi)$ where $R = \\sqrt{a^2+b^2}$, $\\tan\\phi = b/a$. The range of $\\sin$ gives the bounds.',
        hindi: '$a\\sin\\theta + b\\cos\\theta = R\\sin(\\theta + \\phi)$ लिखें जहां $R = \\sqrt{a^2+b^2}$, $\\tan\\phi = b/a$। $\\sin$ की सीमा सीमा देती है।',
          },
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'Find the maximum value of $3\\sin\\theta + 4\\cos\\theta$.',
        hindi: '$3\\sin\\theta + 4\\cos\\theta$ का अधिकतम मान ज्ञात कीजिए।',
          },
          solution: {
            english: '$\\sqrt{3^2 + 4^2} = 5$.',
        hindi: '$\\sqrt{3^2 + 4^2} = 5$।',
          },
        },
      ],
    },
    {
      id: 'identities-misc',
      title: {
        english: 'Useful Conditional Identities (when $A + B + C = \\pi$)',
        hindi: 'उपयोगी सशर्त पहचान (जब $A + B + C = \\pi$)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '$\\sin(A+B) = \\sin C,\\ \\cos(A+B) = -\\cos C,\\ \\tan(A+B) = -\\tan C$',
        hindi: '$\\sin(A+B) = \\sin C,\\ \\cos(A+B) = -\\cos C,\\ \\tan(A+B) = -\\tan C$',
            },
            {
              english: '$\\tan A + \\tan B + \\tan C = \\tan A \\tan B \\tan C$',
        hindi: '$\\tan A + \\tan B + \\tan C = \\tan A \\tan B \\tan C$',
            },
            {
              english: '$\\sin 2A + \\sin 2B + \\sin 2C = 4\\sin A \\sin B \\sin C$',
        hindi: '$\\sin 2A + \\sin 2B + \\sin 2C = 4\\sin A \\sin B \\sin C$',
            },
            {
              english: '$\\cos 2A + \\cos 2B + \\cos 2C = -1 - 4\\cos A \\cos B \\cos C$',
        hindi: '$\\cos 2A + \\cos 2B + \\cos 2C = -1 - 4\\cos A \\cos B \\cos C$',
            },
            {
              english: '$\\cos^2 A + \\cos^2 B + \\cos^2 C + 2\\cos A \\cos B \\cos C = 1$',
        hindi: '$\\cos^2 A + \\cos^2 B + \\cos^2 C + 2\\cos A \\cos B \\cos C = 1$',
            },
          ],
        },
        {
          type: 'note',
          text: {
            english: 'These appear regularly in JEE — especially in problems involving triangles.',
        hindi: 'ये परिणाम JEE में बार-बार पूछे जाते हैं, विशेषकर त्रिभुज-आधारित प्रश्नों में।',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\sin^2\\theta + \\cos^2\\theta = 1',
      desc: {
        english: 'Pythagorean',
        hindi: 'पायथागॉरियन',
      },
    },
    {
      latex: '\\sin(A \\pm B) = \\sin A\\cos B \\pm \\cos A\\sin B',
      desc: {
        english: 'Sum/diff sin',
        hindi: 'योग/अंतर का $\\sin$ सूत्र',
      },
    },
    {
      latex: '\\cos(A \\pm B) = \\cos A\\cos B \\mp \\sin A\\sin B',
      desc: {
        english: 'Sum/diff cos',
        hindi: 'योग/अंतर का $\\cos$ सूत्र',
      },
    },
    {
      latex: '\\sin 2A = 2\\sin A\\cos A',
      desc: {
        english: 'Double angle',
        hindi: 'दोहरा कोण',
      },
    },
    {
      latex: '\\cos 2A = 1 - 2\\sin^2 A',
      desc: {
        english: 'Double angle (sin form)',
        hindi: 'द्विकोण (\\sin रूप)',
      },
    },
    {
      latex: '\\tan 2A = \\tfrac{2\\tan A}{1-\\tan^2 A}',
      desc: {
        english: 'Double angle tan',
        hindi: 'द्विकोण $\\tan$ सूत्र',
      },
    },
    {
      latex: 'a\\sin\\theta + b\\cos\\theta \\in [-\\sqrt{a^2+b^2},\\,\\sqrt{a^2+b^2}]',
      desc: {
        english: 'Linear combination range',
        hindi: 'रैखिक संयोजन सीमा',
      },
    },
    {
      latex: '\\sin C + \\sin D = 2\\sin\\tfrac{C+D}{2}\\cos\\tfrac{C-D}{2}',
      desc: {
        english: 'Sum → product',
        hindi: 'योग → उत्पाद',
      },
    },
  ],
  pyqs: [
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'Identities',
      problem: {
        english: 'If $\\sin\\theta + \\cos\\theta = \\dfrac{1}{2}$, then $\\sin 2\\theta$ equals:',
        hindi: 'यदि $\\sin\\theta + \\cos\\theta = \\dfrac{1}{2}$, तो $\\sin 2\\theta$ बराबर है:',
      },
      answer: {
        english: '$-3/4$',
        hindi: '$-3/4$',
      },
      solution: {
        english: 'Square: $1 + \\sin 2\\theta = 1/4 \\Rightarrow \\sin 2\\theta = -3/4$.',
        hindi: 'वर्ग: $1 + \\sin 2\\theta = 1/4 \\Rightarrow \\sin 2\\theta = -3/4$।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Max-Min',
      problem: {
        english: 'Maximum value of $5\\cos\\theta + 12\\sin\\theta + 7$ is:',
        hindi: '$5\\cos\\theta + 12\\sin\\theta + 7$ का अधिकतम मूल्य है:',
      },
      answer: {
        english: '$20$',
        hindi: '$20$',
      },
      solution: {
        english: 'Max of $5\\cos + 12\\sin = \\sqrt{25 + 144} = 13$. Add 7 → 20.',
        hindi: '$5\\cos\\theta + 12\\sin\\theta$ का अधिकतम मान $\\sqrt{25 + 144} = 13$ होगा। अतः 7 जोड़ने पर अधिकतम मान $20$ है।',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Triple angle',
      problem: {
        english: 'If $\\cos\\theta = -\\dfrac{12}{13}$ and $\\theta \\in (\\pi/2, \\pi)$, find $\\sin 2\\theta$.',
        hindi: 'यदि $\\cos\\theta = -\\dfrac{12}{13}$ और $\\theta \\in (\\pi/2, \\pi)$, तो $\\sin 2\\theta$ खोजें।',
      },
      answer: {
        english: '$-120/169$',
        hindi: '$-120/169$',
      },
      solution: {
        english: '$\\sin\\theta = +5/13$ (Q-II). $\\sin 2\\theta = 2\\sin\\theta\\cos\\theta = 2 \\cdot 5/13 \\cdot (-12/13) = -120/169$.',
        hindi: '$\\sin\\theta = +5/13$ (Q-II). $\\sin 2\\theta = 2\\sin\\theta\\cos\\theta = 2 \\cdot 5/13 \\cdot (-12/13) = -120/169$.',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Conditional',
      problem: {
        english: 'In $\\triangle ABC$, $\\tan A + \\tan B + \\tan C = 6$. Find $\\tan A \\tan B \\tan C$.',
        hindi: '$\\triangle ABC$, $\\tan A + \\tan B + \\tan C = 6$ में। $\\tan A \\tan B \\tan C$ खोजें।',
      },
      answer: {
        english: '$6$',
        hindi: '$6$',
      },
      solution: {
        english: 'Using $A + B + C = \\pi$: $\\tan A + \\tan B + \\tan C = \\tan A \\tan B \\tan C$.',
        hindi: '$A + B + C = \\pi$ का उपयोग करना: $\\tan A + \\tan B + \\tan C = \\tan A \\tan B \\tan C$।',
      },
    },
    {
      year: 2019,
      exam: 'JEE Advanced',
      tag: 'Product to sum',
      problem: {
        english: 'Find $\\cos\\dfrac{\\pi}{7}\\cos\\dfrac{2\\pi}{7}\\cos\\dfrac{4\\pi}{7}$.',
        hindi: '$\\cos\\dfrac{\\pi}{7}\\cos\\dfrac{2\\pi}{7}\\cos\\dfrac{4\\pi}{7}$ खोजें।',
      },
      answer: {
        english: '$-1/8$',
        hindi: '$-1/8$',
      },
      solution: {
        english: 'Let $P$ be the product. Multiply both sides by $\\sin(\\pi/7)$ and use $2\\sin\\theta\\cos\\theta = \\sin 2\\theta$ repeatedly: $P\\sin(\\pi/7) = \\sin(8\\pi/7)/8 = -\\sin(\\pi/7)/8 \\Rightarrow P = -1/8$.',
        hindi: 'मान लीजिए कि $P$ उत्पाद है। दोनों पक्षों को $\\sin(\\pi/7)$ से गुणा करें और $2\\sin\\theta\\cos\\theta = \\sin 2\\theta$ का बार-बार उपयोग करें: $P\\sin(\\pi/7) = \\sin(8\\pi/7)/8 = -\\sin(\\pi/7)/8 \\Rightarrow P = -1/8$।',
      },
    },
  ],
  next: {
    slug: 'sets-relations-functions',
    title: 'Sets, Relations & Functions',
  },
};

export default chapter;
